import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import type { BackButtonListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Categories from './pages/Categories';
import Products from './pages/Products';
import Kitchen from './pages/Kitchen';
import Tables from './pages/Tables';
import Flavors from './pages/Flavors';
import SwaggerPage from './pages/SwaggerUI';
import CashRegister from './pages/CashRegister';
import CashMovements from './pages/CashMovements';
import Settings from './pages/Settings';
import POS from './pages/POS';
import TakeawayBuilderPage from './pages/POS/TakeawayBuilderPage';
import { TakeawayOrderForm } from './features/orders/TakeawayOrderForm';
import { PaymentMethodSettings } from './components/CashRegister/PaymentMethodSettings';
import KpiDashboard from './modules/kpi/pages/Dashboard';
import TimeSeries from './modules/kpi/pages/TimeSeries';
import Top from './modules/kpi/pages/Top';
import Distribution from './modules/kpi/pages/Distribution';
import Funnel from './modules/kpi/pages/Funnel';
import Anomalies from './modules/kpi/pages/Anomalies';
import Docs from './modules/kpi/pages/Docs';
import InventoryDashboard from './modules/inventory/pages/InventoryDashboard';
import ItemsList from './modules/inventory/pages/ItemsList';
import ItemDetail from './modules/inventory/pages/ItemDetail';
import Movements from './modules/inventory/pages/Movements';
import Stock from './modules/inventory/pages/Stock';
import StockSummary from './modules/inventory/pages/StockSummary';
import RecipesPage from './modules/inventory/pages/RecipesPage';
import PurchasesDashboard from './modules/purchases/pages/PurchasesDashboard';
import NewPurchasePage from './modules/purchases/pages/NewPurchasePage';
import PriceHistoryPage from './modules/purchases/pages/PriceHistoryPage';
import SuppliersPage from './modules/purchases/pages/SuppliersPage';
import WhatsappConfigPage from './modules/whatsapp/pages/WhatsappConfigPage';
import WhatsappMonitorPage from './modules/whatsapp/pages/WhatsappMonitorPage';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Logout from './pages/Logout';
import PublicMenu from './pages/PublicMenu';
import ProtectedRoute from './components/ProtectedRoute';

import { AppShell } from './components/AppShell';
import { PwaReloadPrompt } from './components/PwaReloadPrompt';
import { useAuth } from './context/AuthContext';

import { OfflineBadge } from './components/OfflineBadge';
import { useOfflineSyncManager } from './services/useOfflineSyncManager';

function AppRoutes() {
  const { token, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Activar sincronización en segundo plano de PWA
  useOfflineSyncManager();

  // Rutas que no requieren el diseño de la aplicación (AppShell)
  const isPublicPath = ['/', '/login', '/logout', '/menu'].some(p =>
    location.pathname === p || location.pathname === p + '/' || location.pathname.startsWith('/menu/')
  );

  useEffect(() => {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    let removeListener: (() => void) | undefined;

    CapacitorApp.addListener('backButton', (event: BackButtonListenerEvent) => {
      const { canGoBack } = event;

      if (location.pathname === '/' || location.pathname === '/login' || location.pathname.startsWith('/menu/')) {
        CapacitorApp.exitApp();
        return;
      }

      if (canGoBack) {
        navigate(-1);
      } else {
        navigate('/');
      }
    }).then((handle: PluginListenerHandle) => {
      removeListener = () => {
        void handle.remove();
      };
    });

    return () => {
      removeListener?.();
    };
  }, [location.pathname, navigate]);
  
  if (isLoading) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center bg-app gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        <div className="animate-pulse text-lg font-medium text-text-muted">Verificando sesión...</div>
      </div>
    );
  }

  // Si no hay token y se intenta acceder a una ruta privada, redirigir a login inmediatamente
  if (!token && !isPublicPath) {
    return (
      <Routes>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-dvh bg-app text-body">
      <OfflineBadge />
      {token ? (
        isPublicPath && !location.pathname.startsWith('/logout') ? (
          <Routes>
             <Route path="/menu/:tableId" element={<PublicMenu />} />
             <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        ) : (
          <AppShell>
            <Routes>
              <Route path="/categories" element={<ProtectedRoute><Categories /></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
              <Route path="/flavors" element={<ProtectedRoute><Flavors /></ProtectedRoute>} />
              <Route path="/swagger" element={<ProtectedRoute><SwaggerPage /></ProtectedRoute>} />
              <Route path="/tables" element={<ProtectedRoute><Tables /></ProtectedRoute>} />
              <Route path="/pos" element={<ProtectedRoute><POS /></ProtectedRoute>} />
              <Route
                path="/pos/takeaway/new"
                element={
                  <ProtectedRoute>
                    <TakeawayOrderForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pos/build/takeaway"
                element={
                  <ProtectedRoute>
                    <TakeawayBuilderPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/kitchen" element={<ProtectedRoute><Kitchen /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/kpi" element={<ProtectedRoute><KpiDashboard /></ProtectedRoute>} />
              <Route path="/kpi/timeseries" element={<ProtectedRoute><TimeSeries /></ProtectedRoute>} />
              <Route path="/kpi/top" element={<ProtectedRoute><Top /></ProtectedRoute>} />
              <Route path="/kpi/distribution" element={<ProtectedRoute><Distribution /></ProtectedRoute>} />
              <Route path="/kpi/funnel" element={<ProtectedRoute><Funnel /></ProtectedRoute>} />
              <Route path="/kpi/anomalies" element={<ProtectedRoute><Anomalies /></ProtectedRoute>} />
              <Route path="/kpi/docs" element={<ProtectedRoute><Docs /></ProtectedRoute>} />
              <Route path="/inventory" element={<ProtectedRoute><InventoryDashboard /></ProtectedRoute>} />
              <Route path="/inventory/items" element={<ProtectedRoute><ItemsList /></ProtectedRoute>} />
              <Route path="/inventory/items/:id" element={<ProtectedRoute><ItemDetail /></ProtectedRoute>} />
              <Route path="/inventory/movements" element={<ProtectedRoute><Movements /></ProtectedRoute>} />
              <Route path="/inventory/stock" element={<ProtectedRoute><Stock /></ProtectedRoute>} />
              <Route path="/inventory/stock/summary" element={<ProtectedRoute><StockSummary /></ProtectedRoute>} />
              <Route path="/inventory/recipes" element={<ProtectedRoute><RecipesPage /></ProtectedRoute>} />

              {/* ── Compras ── */}
              <Route path="/purchases" element={<ProtectedRoute><PurchasesDashboard /></ProtectedRoute>} />
              <Route path="/purchases/new" element={<ProtectedRoute><NewPurchasePage /></ProtectedRoute>} />
              <Route path="/purchases/prices" element={<ProtectedRoute><PriceHistoryPage /></ProtectedRoute>} />
              <Route path="/purchases/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />

              {/* ── WhatsApp ── */}
              <Route path="/whatsapp" element={<ProtectedRoute><WhatsappMonitorPage /></ProtectedRoute>} />
              <Route path="/whatsapp/config" element={<ProtectedRoute><WhatsappConfigPage /></ProtectedRoute>} />
              <Route path="/auth/status" element={<div>Authenticated</div>} />
              <Route path="/cash" element={<ProtectedRoute><CashRegister /></ProtectedRoute>} />
              <Route path="/cash/movements" element={<ProtectedRoute><CashMovements /></ProtectedRoute>} />
              <Route path="/cash/methods" element={<ProtectedRoute><PaymentMethodSettings /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/logout" element={<Logout />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AppShell>
        )
      ) : (
        <>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/menu/:tableId" element={<PublicMenu />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <ToastContainer
            position="bottom-center"
            newestOnTop
            closeOnClick
            draggable={false}
            style={{
              marginBottom: 'calc(var(--safe-area-bottom, 0px) + var(--keyboard-height, 0px) + 1.5rem)',
            }}
          />
        </>
      )}
      <PwaReloadPrompt />
    </div>
  );
}

export function App() {
  return <AppRoutes />;
}
